import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { fadeInUp, staggerContainer, scaleIn } from "@/motion/variants";
import SignalsSpreadCard from "@/components/charts/SignalsSpreadCard";
import SeasonalityCard from "@/components/charts/SeasonalityCard";
import FreightCard from "@/components/charts/FreightCard";
import { TradingPartnersTrustBar } from "@/components/TrustBar";
import Navigation from "@/components/Navigation";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { TradingTrustStrip } from "@/components/TradingTrustStrip";
import { FeatureBadges } from "@/components/MicroTrustBadges";
import { Package, DollarSign, Sparkles, Target, TrendingUp, Shield, FileText, CreditCard, Database, Ship, Receipt, Users, Search, CheckCircle, Award, Clock, ChevronDown, Building2, FileCheck, Banknote } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Platform = () => {

  return (
    <div className="min-h-screen pt-6">
      <SEO
        title="AI Trading Platform | Predictive Analytics & Compliance Automation | Padmora"
        description="AI-powered trading platform with predictive signals, compliance automation, and document control. Spread radar, LC checker, fixture planner, and KYC engine for faster, safer commodity trading."
        image="/og-platform.jpg"
        canonical="https://padmora.com/platform"
        keywords={[
          "AI trading platform",
          "predictive analytics trading",
          "compliance automation",
          "document control system",
          "spread radar",
          "LC checker",
          "fixture planner",
          "KYC engine",
          "trade automation",
          "machine learning trading",
          "artificial intelligence commodities",
          "automated compliance screening"
        ]}
        aiOptimization={{
          aiKeywords: [
            "artificial intelligence trading platform",
            "machine learning predictive analytics",
            "automated compliance screening",
            "AI document verification",
            "intelligent trade execution",
            "predictive market signals"
          ],
          entities: [
            "Artificial Intelligence",
            "Machine Learning",
            "Predictive Analytics",
            "Compliance Automation",
            "Document Control",
            "United States",
            "Trade Finance"
          ],
          topics: [
            "AI Technology",
            "Trading Platforms",
            "Compliance Management",
            "Predictive Analytics",
            "Automation Solutions"
          ]
        }}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Padmora AI Trading Platform",
          "description": "AI-powered trading platform featuring predictive analytics, compliance automation, and document control for commodity trading operations.",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "featureList": [
            "Spread Radar: Live CFR vs FOB monitoring",
            "LC Checker: Risk flag clauses detection",
            "Fixture Planner: Laycan optimization",
            "KYC Engine: Sanctions screening",
            "Document Control: Automated verification",
            "Compliance Automation: Regulatory compliance"
          ],
          "provider": {
            "@type": "Organization",
            "name": "Padmora",
            "url": "https://padmora.com"
          },
          "screenshot": "https://padmora.com/platform-screenshot.jpg"
        }}
        breadcrumbs={[
          { name: "Home", url: "https://padmora.com/" },
          { name: "Platform", url: "https://padmora.com/platform" }
        ]}
      />
      <Navigation />

      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-6xl font-bold text-slate-900 mb-6"
            >
              Our internal AI platform that makes trading faster and safer
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-slate-600 mb-8"
            >
              LC-backed commodity deals with bank-grade compliance and full document control
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button
                size="lg"
                className="px-8 py-4 text-lg"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                >
                  Send enquiry
                </a>
              </Button>
            </motion.div>
          </motion.div>

          <TradingTrustStrip />

          {/* Partners at bottom of hero */}
          <div className="mt-16">
            <TradingPartnersTrustBar />
          </div>
        </div>
      </section>

      {/* Platform Modules */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="text-3xl font-bold text-center mb-12"
          >
            Platform Modules
          </motion.h2>
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            <motion.div
              variants={fadeInUp}
              className="text-left bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <TrendingUp className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Spread Radar</h3>
              <p className="text-slate-600 mb-4">
                Live CFR vs FOB + freight tracking with real-time market intelligence
              </p>
              <FeatureBadges
                features={[
                  { icon: <TrendingUp className="h-4 w-4" />, label: "Real-time price spreads" },
                  { icon: <Target className="h-4 w-4" />, label: "Market timing signals" },
                  { icon: <DollarSign className="h-4 w-4" />, label: "Freight cost optimization" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="text-left bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <FileText className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4">LC Checker</h3>
              <p className="text-slate-600 mb-4">
                Flags risky clauses before issuance with ICC compliance verification
              </p>
              <FeatureBadges
                features={[
                  { icon: <Shield className="h-4 w-4" />, label: "Risk assessment" },
                  { icon: <CheckCircle className="h-4 w-4" />, label: "Compliance verification" },
                  { icon: <FileText className="h-4 w-4" />, label: "Document validation" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="text-left bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Ship className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Fixture Planner</h3>
              <p className="text-slate-600 mb-4">
                Laycan, routing, and demurrage risk optimization for efficient shipping
              </p>
              <FeatureBadges
                features={[
                  { icon: <Clock className="h-4 w-4" />, label: "Schedule optimization" },
                  { icon: <Ship className="h-4 w-4" />, label: "Route planning" },
                  { icon: <DollarSign className="h-4 w-4" />, label: "Cost minimization" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="text-left bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4">KYC Engine</h3>
              <p className="text-slate-600 mb-4">
                Sanctions & entity matching with automated compliance screening
              </p>
              <FeatureBadges
                features={[
                  { icon: <Shield className="h-4 w-4" />, label: "Sanctions screening" },
                  { icon: <Search className="h-4 w-4" />, label: "Entity verification" },
                  { icon: <CheckCircle className="h-4 w-4" />, label: "Compliance automation" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="text-3xl font-bold text-center mb-12"
          >
            How It Works
          </motion.h2>
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-3 gap-8"
          >
            <motion.div
              variants={fadeInUp}
              className="text-left bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-4">Requirements & KYC</h3>
              <p className="text-slate-600 mb-4">
                Share your commodity specifications and undergo compliance screening
              </p>
              <FeatureBadges
                features={[
                  { icon: <Package className="h-4 w-4" />, label: "Commodity specs" },
                  { icon: <Shield className="h-4 w-4" />, label: "KYC verification" },
                  { icon: <Users className="h-4 w-4" />, label: "Entity matching" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="text-left bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-blue-600">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-4">AI Matching & Signals</h3>
              <p className="text-slate-600 mb-4">
                Our AI analyzes market data and matches you with verified partners
              </p>
              <FeatureBadges
                features={[
                  { icon: <Sparkles className="h-4 w-4" />, label: "Smart matching" },
                  { icon: <Target className="h-4 w-4" />, label: "Market signals" },
                  { icon: <TrendingUp className="h-4 w-4" />, label: "Price intelligence" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="text-left bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl font-bold text-blue-600">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-4">LC & Document Control</h3>
              <p className="text-slate-600 mb-4">
                Execute deals with LC-backed payments and full document tracking
              </p>
              <FeatureBadges
                features={[
                  { icon: <CreditCard className="h-4 w-4" />, label: "LC coordination" },
                  { icon: <FileText className="h-4 w-4" />, label: "Document control" },
                  { icon: <CheckCircle className="h-4 w-4" />, label: "Payment security" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* AI Signals section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="text-3xl font-bold text-center mb-4"
          >
            AI Signals (Illustrative)
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="text-center text-slate-600 mb-12 max-w-2xl mx-auto"
          >
            Predictive analytics and market intelligence powered by AI to inform your trading decisions
          </motion.p>
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-3 gap-8"
          >
            <SignalsSpreadCard />
            <SeasonalityCard />
            <FreightCard />
          </motion.div>
        </div>
      </section>

      {/* Service Packages */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="text-3xl font-bold text-center mb-12"
          >
            Service Packages
          </motion.h2>
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto"
          >
            <motion.div
              variants={fadeInUp}
              className="bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="text-2xl font-bold mb-4 text-slate-900">For Sellers (FOB)</h3>
              <FeatureBadges
                features={[
                  { icon: <FileText className="h-4 w-4" />, label: "SCO/FCO & SPA preparation" },
                  { icon: <Search className="h-4 w-4" />, label: "Buyer discovery (KYC-ready)" },
                  { icon: <Shield className="h-4 w-4" />, label: "Sanctions screening" },
                  { icon: <Ship className="h-4 w-4" />, label: "Shipbroker fixture coordination" },
                  { icon: <Award className="h-4 w-4" />, label: "Insurance & inspection orchestration" },
                  { icon: <CreditCard className="h-4 w-4" />, label: "LC support (SBLC/DLC/at sight)" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>
            <motion.div
              variants={fadeInUp}
              className="bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="text-2xl font-bold mb-4 text-slate-900">For Buyers (CFR/CIF)</h3>
              <FeatureBadges
                features={[
                  { icon: <Search className="h-4 w-4" />, label: "Multiorigin sourcing" },
                  { icon: <Target className="h-4 w-4" />, label: "Predictive indicators" },
                  { icon: <FileText className="h-4 w-4" />, label: "Term sheet & SPA templates" },
                  { icon: <CreditCard className="h-4 w-4" />, label: "LC coordination" },
                  { icon: <Clock className="h-4 w-4" />, label: "Laytime/demurrage guidance" },
                  { icon: <Database className="h-4 w-4" />, label: "Document tracking" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-4"
            >
              Frequently Asked Questions
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-center text-slate-600 mb-12"
            >
              Common questions about our AI-powered trading platform and services
            </motion.p>

            <motion.div variants={fadeInUp}>
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="item-1" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      What is the difference between FOB and CFR/CIF in commodity trading?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p><strong>FOB (Free On Board):</strong> The seller is responsible for delivering goods to the port and loading them onto the vessel. Risk transfers to the buyer once goods cross the ship's rail.</p>
                      <p><strong>CFR (Cost and Freight):</strong> The seller pays for transportation to the destination port, but risk transfers when goods are loaded onto the vessel.</p>
                      <p><strong>CIF (Cost, Insurance, and Freight):</strong> Similar to CFR, but the seller also provides minimum insurance coverage for the goods during transit.</p>
                      <p>Our AI platform helps match FOB sellers with CFR/CIF buyers, optimizing the entire supply chain from origin to destination.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      How do Letters of Credit (LC) work in your platform?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p>We support multiple LC types to facilitate secure transactions:</p>
                      <ul className="list-disc pl-6 space-y-2">
                        <li><strong>LC at Sight:</strong> Payment made immediately upon presentation of compliant documents</li>
                        <li><strong>SBLC (Standby Letter of Credit):</strong> Acts as a guarantee, activated if the buyer fails to perform</li>
                        <li><strong>DLC (Documentary Letter of Credit):</strong> Standard LC requiring specific shipping documents</li>
                        <li><strong>Transferable LC:</strong> Allows the beneficiary to transfer credit to suppliers</li>
                        <li><strong>Back-to-Back LC:</strong> Enables traders to use one LC to open another with their supplier</li>
                      </ul>
                      <p>Our platform coordinates with major banks to streamline LC processes and reduce transaction time.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      What documents are required for commodity trading?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p>Our platform provides standardized templates for all essential trading documents:</p>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-semibold mb-2">Initial Documents:</h4>
                          <ul className="list-disc pl-4 space-y-1 text-sm">
                            <li>NDA/NCNDA (Non-Disclosure Agreement)</li>
                            <li>LOI (Letter of Intent)</li>
                            <li>ICPO (Indicative Commercial Purchase Order)</li>
                            <li>SCO (Sales Contract Offer)</li>
                            <li>FCO (Firm Contract Offer)</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">Final Documents:</h4>
                          <ul className="list-disc pl-4 space-y-1 text-sm">
                            <li>SPA (Sales and Purchase Agreement)</li>
                            <li>COO (Certificate of Origin)</li>
                            <li>Inspection certificates (SGS/Cotecna)</li>
                            <li>Bill of Lading</li>
                            <li>Insurance certificates</li>
                          </ul>
                        </div>
                      </div>
                      <p className="text-sm text-slate-500 mt-3">All templates are private and shared once we initiate a deal. We maintain verifiable e-signature processes with full audit trails.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      How does your AI matching system work?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p>Our AI-powered matching system analyzes multiple factors to connect buyers and sellers:</p>
                      <ul className="list-disc pl-6 space-y-2">
                        <li><strong>Commodity Specifications:</strong> Grade, quality, and HS code matching</li>
                        <li><strong>Volume Requirements:</strong> Minimum lot sizes and delivery schedules</li>
                        <li><strong>Geographic Optimization:</strong> Origin-destination routing for cost efficiency</li>
                        <li><strong>Payment Terms:</strong> LC preferences and bank relationships</li>
                        <li><strong>Market Conditions:</strong> Real-time pricing and freight rates</li>
                        <li><strong>Risk Assessment:</strong> KYC/AML compliance and sanctions screening</li>
                      </ul>
                      <p>The system provides predictive signals on pricing trends, seasonality patterns, and freight sensitivity to help optimize deal timing and margins.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      What is KYC/AML and sanctions screening?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p><strong>KYC (Know Your Customer):</strong> Verification process to identify and verify the identity of trading partners, including company registration, beneficial ownership, and business legitimacy.</p>
                      <p><strong>AML (Anti-Money Laundering):</strong> Procedures to detect and prevent money laundering activities through transaction monitoring and suspicious activity reporting.</p>
                      <p><strong>Sanctions Screening:</strong> Checks against international sanctions lists (OFAC, EU, UN) to ensure compliance with global trade restrictions.</p>
                      <p>All participants on our platform undergo comprehensive screening before any transactions are facilitated. This ensures regulatory compliance and reduces counterparty risk.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-6" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      How do you handle logistics and shipping coordination?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p>Our platform coordinates with verified shipbrokers and logistics partners to handle:</p>
                      <ul className="list-disc pl-6 space-y-2">
                        <li><strong>Vessel Chartering:</strong> Charter Party negotiations and fixture coordination</li>
                        <li><strong>Port Operations:</strong> Loading and discharge port arrangements</li>
                        <li><strong>Laytime Management:</strong> Optimizing loading/unloading schedules to minimize demurrage costs</li>
                        <li><strong>Documentation:</strong> Bill of Lading, shipping instructions, and customs documentation</li>
                        <li><strong>Insurance:</strong> Cargo and marine insurance coordination</li>
                        <li><strong>Inspection Services:</strong> SGS, Cotecna, and other inspection company coordination</li>
                      </ul>
                      <p>We maintain relationships with major shipbrokers and logistics providers to ensure smooth execution from origin to destination.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-7" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      What are the typical deal sizes and commodities you handle?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p>Our platform facilitates deals across various commodity categories:</p>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-semibold mb-2">Agricultural Commodities:</h4>
                          <ul className="list-disc pl-4 space-y-1 text-sm">
                            <li>Grains (Wheat, Corn, Rice)</li>
                            <li>Oilseeds (Soybeans, Sunflower)</li>
                            <li>Sugar and Coffee</li>
                            <li>Palm Oil and Derivatives</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">Industrial Commodities:</h4>
                          <ul className="list-disc pl-4 space-y-1 text-sm">
                            <li>Metals (Copper, Aluminum, Steel)</li>
                            <li>Minerals (Iron Ore, Coal)</li>
                            <li>Chemicals and Fertilizers</li>
                            <li>Energy Products</li>
                          </ul>
                        </div>
                      </div>
                      <p><strong>Deal Sizes:</strong> Typically range from 1,000 MT to 100,000+ MT, depending on commodity type and market conditions. Our AI system optimizes lot sizes based on vessel capacity and market liquidity.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-8" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      How do you ensure transaction security and compliance?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p>Security and compliance are fundamental to our platform:</p>
                      <ul className="list-disc pl-6 space-y-2">
                        <li><strong>Bank-Grade Security:</strong> All transactions are subject to final contract and inspection</li>
                        <li><strong>Verifiable E-Signatures:</strong> Digital signature process with complete audit trail</li>
                        <li><strong>Document Verification:</strong> Automated checks for document authenticity and compliance</li>
                        <li><strong>Payment Security:</strong> LC-backed transactions with major international banks</li>
                        <li><strong>Regulatory Compliance:</strong> Full adherence to international trade regulations and sanctions</li>
                        <li><strong>Risk Management:</strong> Continuous monitoring of counterparty risk and market conditions</li>
                      </ul>
                      <p>We maintain relationships with top-tier banks, insurance providers, and inspection companies to ensure the highest standards of security and compliance.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-9" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      What makes your platform different from traditional commodity trading?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p>Our AI-powered platform revolutionizes commodity trading through:</p>
                      <ul className="list-disc pl-6 space-y-2">
                        <li><strong>Predictive Intelligence:</strong> AI-driven market signals and trend analysis</li>
                        <li><strong>Automated Matching:</strong> Smart buyer-seller pairing based on multiple criteria</li>
                        <li><strong>Standardized Documentation:</strong> Pre-approved templates reducing negotiation time</li>
                        <li><strong>Real-Time Optimization:</strong> Dynamic pricing and logistics optimization</li>
                        <li><strong>Risk Mitigation:</strong> Comprehensive screening and compliance checks</li>
                        <li><strong>Transparency:</strong> Full audit trail and transaction tracking</li>
                        <li><strong>Efficiency:</strong> Reduced time-to-market and lower transaction costs</li>
                      </ul>
                      <p>Traditional trading relies on manual processes and personal networks. Our platform combines human expertise with AI capabilities to create a more efficient, transparent, and profitable trading environment.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-10" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      How do I get started with your trading platform?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p>Getting started is simple and secure:</p>
                      <ol className="list-decimal pl-6 space-y-2">
                        <li><strong>Submit Your Requirements:</strong> Complete our producer, buyer, or trader form with your specific needs</li>
                        <li><strong>KYC Verification:</strong> Provide company documentation and undergo compliance screening</li>
                        <li><strong>Platform Access:</strong> Receive access to our AI matching system and document templates</li>
                        <li><strong>Deal Discovery:</strong> Our AI identifies potential matches and provides market intelligence</li>
                        <li><strong>Transaction Execution:</strong> Execute deals with standardized documentation and secure payments</li>
                      </ol>
                      <p>Our team provides personalized support throughout the process, from initial setup to deal completion. All relationships and partnerships are built on trust, transparency, and mutual success.</p>
                      <p className="text-sm text-slate-500 mt-3">Ready to start? <a href="mailto:michel@padmora.com" className="text-blue-600 hover:underline">Send enquiry</a> to discuss your specific trading needs.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </motion.div>
          </motion.div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-2xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-slate-900 mb-6"
            >
              Ready to Transform Your Trading?
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-slate-600 mb-8"
            >
              Send an enquiry to michel@padmora.com to discuss how our AI-powered platform can enhance your trading operations.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button
                size="lg"
                variant="default"
                className="px-8 py-4 text-lg bg-primary hover:bg-primary/90 text-white"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                >
                  Send enquiry
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Platform;
